import { FC } from 'react'
import {
  InsightCategory,
  SmartInsightsAIRecommendations,
} from 'src/charts/SmartInsightsAIRecommendations'
import { useGetAdsLibrarySmartInsightsQuery } from 'src/reactQuery/useGetAdsLibrarySmartInsightsQuery'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as MergeIcon } from './svg/marge.svg'
import { ReactComponent as NameChangedIcon } from './svg/lucide-lab_coins-exchange.svg'
import { ReactComponent as PageCreatedIcon } from './svg/pageCreated.svg'
import { ReactComponent as PeopleIcon } from './svg/people-who-managed.svg'
import classes from './aboutTab.module.scss'
import { CompetitorsComparison } from '../../Main/CompetitorsComparison'
import { SavedCompetitors } from '../../Main/SavedCompetitors'

interface AboutTabProps {
  hasData?: boolean
  pageId: string
  pageName: string
}

export const AboutTab: FC<AboutTabProps> = ({
  hasData = true,
  pageId,
  pageName,
}) => {
  // Scoped to this advertiser page — without pageId the backend serves the
  // user's brand-wide insights instead.
  const { data: rawInsights, isLoading: smartInsightsLoading } =
    useGetAdsLibrarySmartInsightsQuery(pageId)

  const smartInsightsData = rawInsights
    ? {
        hasInsights: rawInsights.hasInsights,
        insights: rawInsights.insights?.map((i) => ({
          ...i,
          category: i.category as InsightCategory,
        })),
      }
    : undefined
  return (
    <div className={classes.container}>
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>About the advertiser</h3>
        <p className={classes.sectionDesc}>
          In an effort to increase accountability and transparency, Meta is
          sharing more information about Pages and the people who manage them.{' '}
          <a
            className={classes.link}
            href="https://www.facebook.com/help/323314944866264"
            rel="noreferrer"
            target="_blank"
          >
            Learn more about transparency.
          </a>
        </p>
      </div>

      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Page transparency</h3>
        <p className={classes.transparencyDesc}>{pageName}</p>
      </div>

      <div className={classes.grid}>
        {/* Pages and accounts */}
        <div className={classes.card}>
          <h4 className={classes.cardTitle}>Pages and accounts</h4>
          {!hasData ? (
            <span className={classes.noData}>No Data</span>
          ) : (
            <>
              <div className={classes.accountItem}>
                <FacebookIcon className={classes.platformIcon} />
                <div className={classes.accountInfo}>
                  <span className={classes.accountHandle}>@{pageId}</span>
                  <span className={classes.accountMeta}>Followers · Page</span>
                </div>
              </div>
              <div className={classes.accountItem}>
                <InstagramIcon className={classes.platformIcon} />
                <div className={classes.accountInfo}>
                  <span className={classes.accountHandle}>@{pageId}</span>
                  <span className={classes.accountMeta}>Followers</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Page history */}
        <div className={classes.card}>
          <h4 className={classes.cardTitle}>Page history</h4>
          {!hasData ? (
            <span className={classes.noData}>No Data</span>
          ) : (
            <div className={classes.historyList}>
              <div className={classes.historyItem}>
                <MergeIcon className={classes.historyIcon} />
                <span>Merged with 0 other Pages</span>
              </div>
              <div className={classes.historyItem}>
                <NameChangedIcon className={classes.historyIcon} />
                <span>Page name changed 2 times</span>
              </div>
              <div className={classes.historyItem}>
                <PageCreatedIcon className={classes.historyIcon} />
                <span>Page created Oct 22, 2012</span>
              </div>
            </div>
          )}
        </div>

        {/* Organizations */}
        <div className={classes.card}>
          <h4 className={classes.cardTitle}>
            Organizations that manage this Page
          </h4>
          {!hasData ? (
            <span className={classes.noData}>No Data</span>
          ) : (
            <div className={classes.orgContent}>
              <p className={classes.orgText}>
                <strong>{pageName} LLC</strong> is responsible for this Page.
              </p>
              <p className={classes.orgAddress}>Tbilisi, Tbilisi 0177</p>
              <p className={classes.orgAddress}>Georgia</p>
            </div>
          )}
        </div>

        {/* People */}
        <div className={classes.card}>
          <h4 className={classes.cardTitle}>People who manage this Page</h4>
          {!hasData ? (
            <span className={classes.noData}>No Data</span>
          ) : (
            <div className={classes.historyItem}>
              <PeopleIcon className={classes.historyIcon} />
              <span>
                Primary country location for people who manage this Page
                includes: <strong>Georgia (18) France (1)</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      <a
        className={classes.goToPageBtn}
        href={`https://www.facebook.com/${pageId}`}
        rel="noreferrer"
        target="_blank"
      >
        Go to Page
      </a>

      <SavedCompetitors />
      <CompetitorsComparison />
      <SmartInsightsAIRecommendations
        data={smartInsightsData}
        homePageVariant
        isLoading={smartInsightsLoading}
      />
    </div>
  )
}
